/**
 * ProfileService
 *
 * User profile metadata updates.
 * Owns: supabase.auth.updateUser (for user_metadata changes)
 *       userService (for syncing displayName to users table)
 *
 * Handles: updateDisplayName (bridges auth metadata and user table)
 * Future: avatar, preferences, notification settings, etc.
 * Does NOT handle: email changes (EmailChangeService), password changes (PasswordService)
 */

import { userService } from '@/services/UserService';
import type { AuthOperationResponse } from '@/types/auth';

/**
 * ProfileService
 * User profile operations
 */
class ProfileService {
  /**
   * Update display name for authenticated user
   *
   * Updates the user's fullName in the users table via UserService.
   * Returns updated display name for Provider to update React state.
   *
   * @param newDisplayName - New display name
   * @param userId - Current user ID
   * @returns Operation result
   */
  async updateDisplayName(newDisplayName: string, userId: string): Promise<AuthOperationResponse & { displayName?: string }> {
    console.log('🏷️ ProfileService: Display name update request');

    try {
      // Update user's displayName in Supabase via service layer
      const updatedUser = await userService.update(userId, {
        displayName: newDisplayName.trim()
      });

      if (updatedUser) {
        console.log('✅ ProfileService: Display name updated successfully');
        return {
          success: true,
          message: 'Display name updated successfully',
          displayName: newDisplayName.trim()
        };
      } else {
        throw new Error('Failed to update user in database');
      }
    } catch (error) {
      console.error('❌ ProfileService: Display name update error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while updating display name'
      };
    }
  }

  // Future methods:
  // - updateAvatar(avatarUrl: string, userId: string): Promise<AuthOperationResponse>
  // - updatePreferences(preferences: UserPreferences, userId: string): Promise<AuthOperationResponse>
  // - updateNotificationSettings(settings: NotificationSettings, userId: string): Promise<AuthOperationResponse>
}

// Export singleton instance
export const profileService = new ProfileService();
